import React, { useEffect, useState } from 'react';
import { injectIntl, IntlShape } from 'react-intl';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import { get, isEmpty, isNil } from 'lodash';
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import frLocale from 'date-fns/locale/fr';
import * as featureService from 'features/fflip-helpers';
import { FEATURES } from 'features/features';
import moment from 'moment-timezone';
import { formsAreValidated } from 'containers/services/validateForm';
import { feeNoteValidationValidator } from 'containers/LawyerFeeNote/utils';
import messages from 'containers/LawyerFeeNote/messages';
import { translate } from 'containers/services/translate';
import { DOCUMENT, EMPTY_DOCUMENT, FEE_NOTE } from 'containers/data-structure';
import { FormContainer } from 'containers/LawyerFeeNote/styled-components';
import { dateStringToLocalDate } from 'containers/IdentificationPage/utils';
import {
  DOC_MESSAGES,
  DOC_TYPES,
  JUSTIF_DOC_ID,
  PROCEDURE_STEPS,
  PROCEDURE_STEPS_MESSAGE,
} from 'containers/LawyerFeeNote/constants';
import UploadDocumentBloc from 'containers/common/LegalProceedings/JudgementAdd/Forms/JudgementAddPrononcings/UploadDocumentBloc';
import { IUploadedDocument } from 'containers/LawyerLegalProceedingsPage/typings';
import { CorporealDocument } from 'containers/common/LegalProceedings/JudgementAdd/Forms/JudgementAddPrononcings/typings';
import { getDateIgnoreTimezone } from 'containers/common/LegalProceedings/utils';
import { DATE_FORMAT, DATE_PICKET_FORMAT } from 'constants/global';
import { compareDate } from 'containers/CourtManagerLegalProceedings/AssignExpert/utils';
import WarningCard from 'components/InvoiceFeeNoteDialog/WarningCard';

export interface FeeNoteFormProps {
  intl: IntlShape;
  feeNote: any;
  handleDrawerChange: Function;
  handleSubmit?: Function;
  handleCancel?: Function;
  handleDelete?: Function;
  inputProps?: any;
  disableInput?: boolean;
  inputLabelProps?: boolean;
  isManager: boolean;
  //UPLOAD PROPS
  uploadDocument: Function;
  uploadedDocument: IUploadedDocument;
  resetUploadedDocument: Function;
  setDocumentId: Function;
  documentId: string;
  updateValidation: Function;
  invoiceNumber: any;
  setInvoiceNumber: Function;
  invoiceDate: any;
  setInvoiceDate: Function;
  setInvoiceReceptionDate?: Function;
  invoiceReceptionDate: any;
  isIntervenant?: boolean;
  limitInvoiceDate: any;
}
const FeeNoteForm: React.FC<FeeNoteFormProps> = (props): JSX.Element => {
  const [justificatif, setJusitificatif] = useState({
    ...EMPTY_DOCUMENT,
    isIndexed: false,
  });

  React.useEffect(() => {
    props.handleDrawerChange({
      ...props.feeNote,
      document: {
        ...props.feeNote.document,
        isIndexed: false,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (field: string) => (
    event: React.ChangeEvent<{
      value: any;
    }>,
  ) => {
    props.handleDrawerChange({
      ...props.feeNote,
      [FEE_NOTE.CREATED_AT]: new Date(),
      [field]: event.target.value,
      document: {
        ...props.feeNote.document,
        ...(field === DOCUMENT.TYPE
          ? { [field]: event.target.value }
          : undefined),
      },
    });
  };
  useEffect(() => {
    props.updateValidation(
      formsAreValidated(feeNoteValidationValidator(props.feeNote) || {}) &&
        !isEmpty(get(justificatif, 'url', '')) &&
        (featureService.hasFeature(FEATURES.LIMIT_INVOICE_DATE) &&
        props.isIntervenant
          ? !isNil(props.invoiceDate) &&
            !isEmpty(props.invoiceDate) &&
            props.invoiceDate != '' &&
            props.invoiceDate != 'Invalid date' &&
            props.invoiceDate &&
            compareDate(moment().format('YYYY-MM-DD'), props.invoiceDate) <
              props.limitInvoiceDate &&
            !isNil(props.invoiceNumber) &&
            props.invoiceNumber != ''
          : true),
    );
  }, [justificatif, props.feeNote, props.invoiceNumber, props.invoiceDate]);
  const setDocument = (docSelected: CorporealDocument) => {
    setJusitificatif({ ...justificatif, ...docSelected });
    props.handleDrawerChange({
      ...props.feeNote,
      document: docSelected,
    });
    props.resetUploadedDocument();
  };

  const handleChangeInvoiceNumber = (
    event: React.ChangeEvent<{
      name?: string;
      value?: unknown;
    }>,
  ) => {
    props.setInvoiceNumber(event.target.value);
  };

  const handleChangeInvoiceDate = (date: Date | null) => {
    const theDate = getDateIgnoreTimezone(date);
    const invoiceDate = moment(theDate as Date).format(DATE_FORMAT);
    props.setInvoiceDate(invoiceDate);
  };

  const handleChangeInvoiceReceptionDate = (date: Date | null) => {
    const theDate = getDateIgnoreTimezone(date);
    const invoiceDate = moment(theDate as Date).format(DATE_FORMAT);
    props.setInvoiceReceptionDate && props.setInvoiceReceptionDate(invoiceDate);
  };

  return (
    <FormContainer>
      <div className="subContainer fee-note-form">
        <TextField
          label={translate({ ...messages.claimNumber }, props)}
          className="input"
          name={FEE_NOTE.CLAIM_ID}
          value={get(props.feeNote, [FEE_NOTE.CLAIM, 'claimNumber']) || ''}
          disabled={true}
        ></TextField>
        <FormControl className="input">
          <InputLabel id="procedure_nature-label-id">
            {translate(messages.step, props)}
          </InputLabel>
          <Select
            labelId="procedure_nature-label-id"
            name={FEE_NOTE.STEP}
            value={get(props.feeNote, [FEE_NOTE.STEP], '')}
            disabled={true}
          >
            {Object.keys(PROCEDURE_STEPS).map((key: any, index: number) => (
              <MenuItem key={index} value={get(PROCEDURE_STEPS, key)}>
                {translate({ ...get(PROCEDURE_STEPS_MESSAGE, key) }, props)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <MuiPickersUtilsProvider utils={DateFnsUtils} locale={frLocale}>
          <KeyboardDatePicker
            autoOk
            variant="inline"
            invalidDateMessage={
              '' // add error
            }
            format="dd/MM/yyyy"
            id="date-picker-inline-victim-date-of-birth"
            label={translate({ ...messages.creationDate }, props)}
            className="input"
            name={FEE_NOTE.CREATED_AT}
            value={dateStringToLocalDate(new Date().toString())}
            onChange={() => null}
            disabled={true}
          />
        </MuiPickersUtilsProvider>
        <Typography className="subtitle">
          {translate({ ...messages.additionalFees }, props)}
        </Typography>

        <TextField
          label={translate({ ...messages.feeNoteFrais }, props)}
          className="input"
          type="number"
          name={FEE_NOTE.COSTS}
          value={get(props.feeNote, [FEE_NOTE.COSTS]) || ''}
          onChange={handleChange(FEE_NOTE.COSTS)}
          InputProps={props.inputProps}
          disabled={props.disableInput}
          InputLabelProps={{
            shrink: props.inputLabelProps,
          }}
          error={false}
          helperText=""
        />

        <FormControl className="input">
          <InputLabel id="fee-note-doc-type-label">
            {translate({ ...get(messages, 'documentType') }, props)}
          </InputLabel>
          <Select
            labelId="fee-note-doc-type-select"
            onChange={handleChange(DOCUMENT.TYPE)}
            name={DOCUMENT.TYPE}
            value={get(props.feeNote, [FEE_NOTE.DOCUMENT, DOCUMENT.TYPE], '')}
          >
            {DOC_TYPES.map((type: any, index: number) => (
              <MenuItem key={index} value={type}>
                {translate({ ...get(DOC_MESSAGES, type) }, props)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {get(props.feeNote, [FEE_NOTE.DOCUMENT, DOCUMENT.TYPE], '') ===
          'EXCEPTIONAL' && (
          <TextField
            label={translate({ ...messages.comment }, props)}
            className="input"
            style={{ marginTop: '20px' }}
            name={FEE_NOTE.COMMENT}
            value={get(props.feeNote, [FEE_NOTE.COMMENT]) || ''}
            multiline
            rows={2}
            onChange={handleChange(FEE_NOTE.COMMENT)}
            InputProps={props.inputProps}
            disabled={props.disableInput}
            InputLabelProps={{
              shrink: props.inputLabelProps,
            }}
            error={false}
            helperText=""
          ></TextField>
        )}
        {featureService.hasFeature(FEATURES.LIMIT_INVOICE_DATE) && (
          <>
            <Typography className="subtitle">
              {translate({ ...messages.invoiceTitle }, props)}
            </Typography>
            <TextField
              label={translate(messages.invoiceNumberField, props)}
              name={'INVOICE_NUMBER'}
              value={props.invoiceNumber}
              fullWidth
              className="input"
              onChange={handleChangeInvoiceNumber}
            />
            {!props.isIntervenant &&
              compareDate(
                moment().format('YYYY-MM-DD'),
                moment(props.invoiceDate).format('YYYY-MM-DD'),
              ) >= props.limitInvoiceDate &&
              !isNil(props.invoiceDate) && (
                <WarningCard
                  message={translate(
                    messages.warningInvoiceDateManager,
                    props,
                    {
                      limitDaysManager: props.limitInvoiceDate,
                    },
                  )}
                />
              )}
            <MuiPickersUtilsProvider utils={DateFnsUtils} locale={frLocale}>
              <KeyboardDatePicker
                autoOk
                variant="inline"
                format={DATE_PICKET_FORMAT}
                id="invoice-date"
                fullWidth
                className="input"
                onChange={handleChangeInvoiceDate}
                value={dateStringToLocalDate(props.invoiceDate)}
                label={translate(messages.invoiceDateField, props)}
                error={
                  featureService.hasFeature(FEATURES.LIMIT_INVOICE_DATE) &&
                  props.isIntervenant &&
                  compareDate(
                    moment().format('YYYY-MM-DD'),
                    moment(props.invoiceDate).format('YYYY-MM-DD'),
                  ) >= props.limitInvoiceDate
                }
                helperText={
                  featureService.hasFeature(FEATURES.LIMIT_INVOICE_DATE) &&
                  props.isIntervenant &&
                  !isNil(props.invoiceDate) &&
                  compareDate(
                    moment().format('YYYY-MM-DD'),
                    moment(props.invoiceDate).format('YYYY-MM-DD'),
                  ) >= props.limitInvoiceDate &&
                  translate(messages.errorInvoiceDateIntervenant, props, {
                    limitDaysIntervenant: props.limitInvoiceDate,
                  })
                }
              />
            </MuiPickersUtilsProvider>
            {props.isManager && (
              <MuiPickersUtilsProvider utils={DateFnsUtils} locale={frLocale}>
                <KeyboardDatePicker
                  autoOk
                  variant="inline"
                  format={DATE_PICKET_FORMAT}
                  id="invoice-date"
                  className="input"
                  fullWidth
                  onChange={handleChangeInvoiceReceptionDate}
                  value={dateStringToLocalDate(props.invoiceReceptionDate)}
                  label={translate(messages.invoiceReceptionDateField, props)}
                />
              </MuiPickersUtilsProvider>
            )}
          </>
        )}
        <UploadDocumentBloc
          index={0}
          document={justificatif}
          setDocument={setDocument}
          resetUploadedDocument={props.resetUploadedDocument}
          uploadDocument={props.uploadDocument}
          uploadedDocument={props.uploadedDocument}
          docType={get(props.feeNote, [FEE_NOTE.DOCUMENT, DOCUMENT.TYPE], '')}
          setDocumentId={props.setDocumentId}
          docId={JUSTIF_DOC_ID}
        />
      </div>
    </FormContainer>
  );
};

export default injectIntl(FeeNoteForm);
